/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { ShieldBanIcon } from "lucide-react"
import { Link } from "react-router"

import { cn } from "@/utils/cn"

import { AppRoutes } from "../../routes/routes"
import { buttonVariants } from "../primitives/button"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"

/**
 * The page for a route the user's roles don't allow. There are two ways in.
 *
 * `useRouteEffect` navigates here client-side when none of the user's roles
 * appear in `rolesForRoute` for the requested route.
 *
 * A full load of `/access-denied`, such as a reload. `asyncInit` loads the
 * session first, or sends a signed-out visitor to login.
 *
 * Either way the session is there, so the page renders beside the sidebar. No
 * HTTP 403 is involved. The check runs in the browser, and the backend still
 * authorizes every API call on its own.
 */
export const AccessDeniedView = () => (
    // TODO: Remove `border-0` when preflight loads at cutover (#1166). Until then the stock
    // `border-dashed` gets the browser's 3px default border width and draws a frame.
    <Empty className="max-inline-7xl min-h-dvh border-0">
        <EmptyHeader>
            <EmptyMedia>
                <ShieldBanIcon className="size-10 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>
                <h1 className="m-0 text-2xl font-bold">Access denied</h1>
            </EmptyTitle>
            <EmptyDescription className="text-foreground/70">
                <p className="mb-1 font-semibold text-foreground">
                    You do not have the required permissions to access this page.
                </p>
                <p className="mb-0">Please contact your administrator if you require additional access privileges.</p>
            </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
            {/*
             * A router `Link`, where the 404 page uses a plain `<a>`. The session is
             * already loaded here, so a full page load would only refetch it.
             */}
            <Link
                to={AppRoutes.Dashboard}
                className={cn(buttonVariants({ size: "lg" }), "font-bold uppercase no-underline")}
            >
                Go to Dashboard
            </Link>
        </EmptyContent>
    </Empty>
)
